import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calculator as CalcIcon, RotateCcw, Plus, Trash2, DollarSign, TrendingUp, Users, Briefcase, ChevronDown, ChevronUp, Download, FileText, Save, FolderOpen, ArrowLeft, Send, Handshake, CheckCircle2, Paperclip, X } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { generateCalculatorPDF } from '../lib/calculatorPdfGenerator';
import { projectsApi, proposalsApi, partnersApi, dealFlowApi } from '../lib/api';
import { toast } from 'sonner';

// -- Utility helpers --
const fmt = (v) => {
  if (v == null || isNaN(v)) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};
const fmtPct = (v, decimals = 2) => {
  if (v == null || isNaN(v)) return '0%';
  return `${(v * 100).toFixed(decimals)}%`;
};
const parseNum = (v) => {
  const n = parseFloat(String(v).replace(/[,$%]/g, ''));
  return isNaN(n) ? 0 : n;
};

// -- Currency Input Component --
const CurrencyInput = ({ label, value, onChange, prefix = '$', testId, className = '' }) => (
  <div className={className}>
    {label && <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">{label}</Label>}
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{prefix}</span>
      <Input
        type="text"
        inputMode="decimal"
        value={value === 0 ? '' : value.toLocaleString('en-US')}
        onChange={(e) => onChange(parseNum(e.target.value))}
        className="pl-7 h-9 text-sm font-mono tabular-nums"
        data-testid={testId}
      />
    </div>
  </div>
);

// -- Percent Input Component --
const PercentInput = ({ label, value, onChange, testId, className = '' }) => (
  <div className={className}>
    {label && <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">{label}</Label>}
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        value={value === 0 ? '' : value}
        onChange={(e) => onChange(parseNum(e.target.value))}
        className="pr-7 h-9 text-sm font-mono tabular-nums"
        data-testid={testId}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
    </div>
  </div>
);

// -- Stat Display --
const StatLine = ({ label, value, highlight = false, negative = false }) => (
  <div className={`flex justify-between items-center py-1.5 ${highlight ? 'font-semibold' : ''}`}>
    <span className={`text-sm ${highlight ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
    <span className={`text-sm font-mono tabular-nums ${highlight ? (negative ? 'text-red-600' : 'text-emerald-700') : 'text-foreground'}`}>{value}</span>
  </div>
);

// Default tier presets
const DEFAULT_OPTION_A = [
  { startDay: 1, endDay: 180, investorSplit: 40 },
  { startDay: 181, endDay: 270, investorSplit: 30 },
  { startDay: 271, endDay: 365, investorSplit: 40 },
  { startDay: 366, endDay: 400, investorSplit: 50 },
  { startDay: 401, endDay: '', investorSplit: 60 },
];

const DEFAULT_OPTION_B = [
  { startDay: 1, endDay: 180, investorSplit: 35 },
  { startDay: 181, endDay: 270, investorSplit: 40 },
  { startDay: 271, endDay: 365, investorSplit: 45 },
  { startDay: 366, endDay: 456, investorSplit: 50 },
  { startDay: 457, endDay: '', investorSplit: 55 },
];

const DEFAULT_DEAL = {
  purchasePrice: 100000,
  survey: 2500,
  soil: 1500,
  otherDD: 0,
  purchaseClosingCosts: 1400,
  improvements: 0,
  estimatedSalePrice: 155000,
  saleClosingCosts: 1700,
  saleCommission: 6,
};

const DEFAULT_FUNDING = {
  lfPurchasePrice: 0,
  lfDDCosts: 0,
  lfImprovements: 0,
  lfClosingCosts: 50,
};

// -- Tier Table --
const TierEditor = ({ tiers, setTiers, label, testIdPrefix }) => {
  const updateTier = (index, field, value) => {
    const next = [...tiers];
    next[index] = { ...next[index], [field]: value };
    setTiers(next);
  };
  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const newStart = last ? (parseNum(last.endDay) || parseNum(last.startDay)) + 1 : 1;
    setTiers([...tiers, { startDay: newStart, endDay: '', investorSplit: 50 }]);
  };
  const removeTier = (i) => {
    if (tiers.length <= 1) return;
    setTiers(tiers.filter((_, idx) => idx !== i));
  };

  return (
    <div data-testid={`${testIdPrefix}-tier-editor`}>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label>
        <Button variant="ghost" size="sm" onClick={addTier} className="h-7 text-xs gap-1" data-testid={`${testIdPrefix}-add-tier`}>
          <Plus className="w-3 h-3" /> Add Tier
        </Button>
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1">
          <span>From Day</span>
          <span>To Day</span>
          <span>Investor %</span>
          <span></span>
        </div>
        {tiers.map((tier, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_28px] gap-2 items-center">
            <Input
              type="number"
              value={tier.startDay}
              onChange={(e) => updateTier(i, 'startDay', parseNum(e.target.value))}
              className="h-8 text-xs font-mono"
              data-testid={`${testIdPrefix}-tier-${i}-start`}
            />
            <Input
              type="text"
              value={tier.endDay === '' ? '' : tier.endDay}
              placeholder="∞"
              onChange={(e) => updateTier(i, 'endDay', e.target.value === '' ? '' : parseNum(e.target.value))}
              className="h-8 text-xs font-mono"
              data-testid={`${testIdPrefix}-tier-${i}-end`}
            />
            <div className="relative">
              <Input
                type="number"
                value={tier.investorSplit}
                onChange={(e) => updateTier(i, 'investorSplit', parseNum(e.target.value))}
                className="h-8 text-xs font-mono pr-6"
                data-testid={`${testIdPrefix}-tier-${i}-split`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">%</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeTier(i)}
              disabled={tiers.length <= 1}
              className="h-7 w-7 text-muted-foreground hover:text-red-600"
              data-testid={`${testIdPrefix}-tier-${i}-remove`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// -- Results Table for an Option --
const ResultsTable = ({ tierResults, totalInvestorCapital, grossProfit, testIdPrefix }) => (
  <div className="overflow-x-auto" data-testid={`${testIdPrefix}-results`}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Range</th>
          <th className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Inv. Split</th>
          <th className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Investor $</th>
          <th className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">CoC</th>
          <th className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ann. ROI</th>
          <th className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">LF Split</th>
          <th className="text-right py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">LF $</th>
        </tr>
      </thead>
      <tbody>
        {tierResults.map((r, i) => (
          <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
            <td className="py-2.5 px-2 font-mono text-xs">
              {r.startDay}–{r.endDay || '∞'} days
            </td>
            <td className="py-2.5 px-2 text-right font-mono text-xs">{r.investorSplit}%</td>
            <td className="py-2.5 px-2 text-right font-mono text-xs text-emerald-700">{fmt(r.investorAmount)}</td>
            <td className="py-2.5 px-2 text-right font-mono text-xs">{fmtPct(r.investorROI)}</td>
            <td className="py-2.5 px-2 text-right font-mono text-xs text-blue-600">
              {r.annualizedROI != null ? fmtPct(r.annualizedROI) : '—'}
            </td>
            <td className="py-2.5 px-2 text-right font-mono text-xs">{r.lfSplit}%</td>
            <td className="py-2.5 px-2 text-right font-mono text-xs font-semibold">{fmt(r.lfAmount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// -- Collapsible Section --
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="rounded-sm border" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm font-semibold uppercase tracking-wide">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-5 px-5">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

// ── Multi-partner promote helpers (Phase 2) ──────────────────────────────
const AllotmentSummary = ({ target, selectedIds, allotments }) => {
  let assigned = 0;
  let openCount = 0;
  selectedIds.forEach((id) => {
    const a = allotments[id] || { basis: 'open' };
    if (a.basis === 'percent' && a.value) assigned += target * (Number(a.value) / 100);
    else if (a.basis === 'capital' && a.value) assigned += Number(a.value);
    else openCount += 1;
  });
  const remaining = Math.max(0, target - assigned);
  const over = assigned > target + 0.5;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" data-testid="promote-allot-summary">
      <span className="text-muted-foreground">Investor capital target: <span className="font-mono font-semibold text-foreground">{fmt(target)}</span></span>
      <span className="text-muted-foreground">Pre-assigned: <span className={`font-mono font-semibold ${over ? 'text-red-600' : 'text-foreground'}`}>{fmt(assigned)}</span></span>
      <span className="text-muted-foreground">Open{openCount ? ` (${openCount})` : ''}: <span className="font-mono font-semibold text-foreground">{fmt(remaining)}</span></span>
      {over && <span className="text-red-600 font-medium" data-testid="promote-over-allocated">Over-allocated</span>}
    </div>
  );
};

const PartnerAllotmentRow = ({ partnerId, target, allotment, onChange }) => {
  const a = allotment || { basis: 'open', value: '' };
  const setBasis = (basis) => onChange(partnerId, { basis, value: basis === 'open' ? '' : (a.value || '') });
  const setValue = (value) => onChange(partnerId, { ...a, value });
  const dollarEquiv =
    a.basis === 'percent' && a.value ? target * (Number(a.value) / 100)
      : a.basis === 'capital' && a.value ? Number(a.value)
        : null;
  return (
    <div className="mt-1.5 pl-7 flex flex-wrap items-center gap-2" data-testid={`allotment-row-${partnerId}`}>
      <div className="inline-flex rounded-sm border overflow-hidden text-[11px]">
        {['open', 'percent', 'capital'].map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBasis(b)}
            className={`px-2 py-1 ${a.basis === b ? 'bg-[#C68E5D] text-white' : 'bg-background text-muted-foreground hover:bg-muted'} ${b !== 'open' ? 'border-l' : ''}`}
            data-testid={`allotment-basis-${partnerId}-${b}`}
          >
            {b === 'open' ? 'Open' : b === 'percent' ? '%' : '$'}
          </button>
        ))}
      </div>
      {a.basis !== 'open' && (
        <input
          type="number"
          min="0"
          value={a.value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={a.basis === 'percent' ? '% of investor side' : '$ amount'}
          className="h-7 w-36 border rounded-sm px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          data-testid={`allotment-value-${partnerId}`}
        />
      )}
      {dollarEquiv != null && <span className="text-[10px] text-muted-foreground font-mono">≈ {fmt(dollarEquiv)}</span>}
      {a.basis === 'open' && <span className="text-[10px] text-muted-foreground">Partner proposes amount on accept</span>}
    </div>
  );
};


export function Calculator() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project_id');
  const proposalId = searchParams.get('proposal_id');

  // -- State --
  const [deal, setDeal] = useState(DEFAULT_DEAL);
  const [funding, setFunding] = useState(DEFAULT_FUNDING);
  const [optionATiers, setOptionATiers] = useState(DEFAULT_OPTION_A);
  const [optionBTiers, setOptionBTiers] = useState(DEFAULT_OPTION_B);
  const [activeTab, setActiveTab] = useState('option-a');
  const [scenarioName, setScenarioName] = useState('');
  const [linkedProject, setLinkedProject] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(proposalId || null);
  const [saving, setSaving] = useState(false);
  const [savedProposals, setSavedProposals] = useState([]);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  // Carried from Deal Analysis "Push to Proposal" to drive subdivide vs flip
  // contract template selection downstream. No dedicated UI on this page —
  // owners can flip it on the promoted deal card itself if needed.
  const [isSubdivide, setIsSubdivide] = useState(false);
  const [parcels, setParcels] = useState([]);

  // Load project data if project_id is provided
  useEffect(() => {
    if (projectId) {
      projectsApi.getOne(projectId).then(res => {
        const p = res.data;
        setLinkedProject(p);
        // Only auto-populate if we're NOT loading a specific proposal
        if (!proposalId) {
          setScenarioName(p.name || '');
          setDeal(prev => ({
            ...prev,
            purchasePrice: p.purchase_price || 0,
            estimatedSalePrice: p.sale_price || p.estimated_sale_price || 0,
          }));
        }
      }).catch(() => {
        toast.error('Failed to load project data');
      });
    }
  }, [projectId, proposalId]);

  // Load specific proposal if proposal_id is provided
  useEffect(() => {
    if (proposalId) {
      proposalsApi.get(proposalId).then(res => {
        const p = res.data;
        setCurrentProposalId(p.id);
        setScenarioName(p.name || '');
        if (p.deal) setDeal({ ...DEFAULT_DEAL, ...p.deal });
        if (p.funding) setFunding({ ...DEFAULT_FUNDING, ...p.funding });
        if (p.option_a_tiers?.length) setOptionATiers(p.option_a_tiers);
        if (p.option_b_tiers?.length) setOptionBTiers(p.option_b_tiers);
        if (typeof p.is_subdivide === 'boolean') setIsSubdivide(p.is_subdivide);
        if (Array.isArray(p.parcels)) setParcels(p.parcels);
      }).catch(() => {
        toast.error('Failed to load proposal');
      });
    }
  }, [proposalId]);

  // Pick up data pushed from Deal Analysis
  useEffect(() => {
    const pushed = sessionStorage.getItem('pushToProposal');
    if (pushed) {
      try {
        const data = JSON.parse(pushed);
        if (data.scenarioName) setScenarioName(data.scenarioName);
        if (data.deal) setDeal({ ...DEFAULT_DEAL, ...data.deal });
        if (data.funding) setFunding({ ...DEFAULT_FUNDING, ...data.funding });
        if (data.optionATiers?.length) setOptionATiers(data.optionATiers);
        if (data.optionBTiers?.length) setOptionBTiers(data.optionBTiers);
        if (typeof data.isSubdivide === 'boolean') setIsSubdivide(data.isSubdivide);
        if (Array.isArray(data.parcels)) setParcels(data.parcels);
        setCurrentProposalId(null);
        toast.success(`Loaded analysis "${data.scenarioName}" — review and save as proposal`);
      } catch (e) {
        console.error('Failed to parse pushed data:', e);
      }
      sessionStorage.removeItem('pushToProposal');
    }
  }, []);

  // Load saved proposals list
  useEffect(() => {
    proposalsApi.list(projectId || undefined).then(res => {
      setSavedProposals(res.data || []);
    }).catch(() => {});
  }, [projectId, currentProposalId]);

  const handleSaveProposal = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId || linkedProject?.id || null,
        project_name: linkedProject?.name || '',
        name: scenarioName || 'Untitled Proposal',
        deal,
        funding,
        option_a_tiers: optionATiers,
        option_b_tiers: optionBTiers,
        is_subdivide: isSubdivide,
        parcels,
      };

      if (currentProposalId) {
        await proposalsApi.update(currentProposalId, payload);
        toast.success('Proposal saved');
      } else {
        const res = await proposalsApi.create(payload);
        setCurrentProposalId(res.data.id);
        toast.success('Proposal created');
      }
      // Refresh list
      const listRes = await proposalsApi.list(projectId || undefined);
      setSavedProposals(listRes.data || []);
    } catch (err) {
      toast.error('Failed to save proposal');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadProposal = async (id) => {
    try {
      const res = await proposalsApi.get(id);
      const p = res.data;
      setCurrentProposalId(p.id);
      setScenarioName(p.name || '');
      if (p.deal) setDeal({ ...DEFAULT_DEAL, ...p.deal });
      if (p.funding) setFunding({ ...DEFAULT_FUNDING, ...p.funding });
      if (p.option_a_tiers?.length) setOptionATiers(p.option_a_tiers);
      if (p.option_b_tiers?.length) setOptionBTiers(p.option_b_tiers);
      setShowLoadDialog(false);
      toast.success(`Loaded: ${p.name}`);
    } catch (err) {
      toast.error('Failed to load proposal');
    }
  };

  const handleDeleteProposal = async (id) => {
    if (!window.confirm('Delete this saved proposal?')) return;
    try {
      await proposalsApi.delete(id);
      if (currentProposalId === id) setCurrentProposalId(null);
      const listRes = await proposalsApi.list(projectId || undefined);
      setSavedProposals(listRes.data || []);
      toast.success('Proposal deleted');
    } catch (err) {
      toast.error('Failed to delete proposal');
    }
  };

  const handleNewProposal = () => {
    setCurrentProposalId(null);
    resetAll();
    if (linkedProject) {
      setScenarioName(linkedProject.name || '');
      setDeal(prev => ({
        ...prev,
        purchasePrice: linkedProject.purchase_price || 0,
        estimatedSalePrice: linkedProject.sale_price || linkedProject.estimated_sale_price || 0,
      }));
    }
  };

  // -- Promote to Partners --
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [activePartners, setActivePartners] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  // Multi-partner allotments (Phase 2): { [partnerId]: { basis: 'open'|'percent'|'capital', value: number|'' } }
  const [partnerAllotments, setPartnerAllotments] = useState({});
  const [promoteDescription, setPromoteDescription] = useState('');
  const [promoteVideoUrl, setPromoteVideoUrl] = useState('');
  const [promoteScenario, setPromoteScenario] = useState('A');
  const [promoting, setPromoting] = useState(false);
  // Attachments: array of {id, filename, size, storage_path, content_type, uploaded_at}
  // returned by /promoted-deals/upload-attachment. Kept in local state until
  // the user clicks Promote, at which point they're sent in the request body.
  const [promoteAttachments, setPromoteAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const handleOpenPromote = async () => {
    if (!currentProposalId) {
      toast.error('Save the proposal first before promoting');
      return;
    }
    try {
      const res = await partnersApi.list('active');
      setActivePartners(res.data || []);
      setSelectedPartnerIds([]);
      setPartnerAllotments({});
      setPromoteDescription('');
      setPromoteVideoUrl('');
      setPromoteScenario('A');
      setPromoteAttachments([]);
      setShowPromoteDialog(true);
    } catch (err) {
      toast.error('Failed to load partners');
    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';  // reset input so the same file can be re-picked
    setUploadingAttachment(true);
    try {
      // Upload sequentially to surface individual errors clearly.
      for (const f of files) {
        if (!f.name.toLowerCase().endsWith('.pdf')) {
          toast.error(`${f.name}: only PDF files are allowed`);
          continue;
        }
        if (f.size > 25 * 1024 * 1024) {
          toast.error(`${f.name}: file too large (max 25MB)`);
          continue;
        }
        try {
          const res = await dealFlowApi.uploadAttachment(f);
          setPromoteAttachments(prev => [...prev, res.data]);
          toast.success(`Uploaded ${f.name}`);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
        }
      }
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (id) => {
    setPromoteAttachments(prev => prev.filter(a => a.id !== id));
  };

  const updateAllotment = (id, val) => {
    setPartnerAllotments(prev => ({ ...prev, [id]: val }));
  };

  const handlePromote = async () => {
    if (selectedPartnerIds.length === 0) {
      toast.error('Select at least one partner');
      return;
    }
    setPromoting(true);
    try {
      const partner_allotments = selectedPartnerIds.map(pid => {
        const a = partnerAllotments[pid] || { basis: 'open' };
        return {
          partner_id: pid,
          basis: a.basis || 'open',
          value: a.basis === 'open' || a.value === '' || a.value == null ? null : Number(a.value),
        };
      });
      const res = await dealFlowApi.promote({
        proposal_id: currentProposalId,
        partner_ids: selectedPartnerIds,
        description: promoteDescription,
        video_url: promoteVideoUrl,
        selected_scenario: promoteScenario,
        attachments: promoteAttachments,
        investor_capital_target: Math.round((calcs.totalInvestorCapital || 0) * 100) / 100,
        partner_allotments,
      });
      const attachMsg = res.data.attachments_emailed
        ? ` (${res.data.attachments_emailed} attachment${res.data.attachments_emailed !== 1 ? 's' : ''} included)`
        : '';
      toast.success(`Deal promoted to ${res.data.emails_sent} partner(s)${attachMsg}`);
      setShowPromoteDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote deal');
    } finally {
      setPromoting(false);
    }
  };

  const updateDeal = useCallback((field, value) => {
    setDeal(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateFunding = useCallback((field, value) => {
    setFunding(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetAll = () => {
    setDeal(DEFAULT_DEAL);
    setFunding(DEFAULT_FUNDING);
    setAmCommission(0);
    setOptionATiers(DEFAULT_OPTION_A);
    setOptionBTiers(DEFAULT_OPTION_B);
  };

  // -- Derived Calculations --
  const calcs = useMemo(() => {
    const totalDD = deal.survey + deal.soil + deal.otherDD;
    const saleCommissionAmount = deal.estimatedSalePrice * (deal.saleCommission / 100);
    const grossSalesRevenue = deal.estimatedSalePrice - deal.saleClosingCosts - saleCommissionAmount;
    const totalCosts = deal.purchasePrice + totalDD + deal.purchaseClosingCosts + deal.improvements;
    const grossProfit = grossSalesRevenue - totalCosts;

    // Investor capital (what investor funds)
    const invPP = deal.purchasePrice * ((100 - funding.lfPurchasePrice) / 100);
    const invDD = totalDD * ((100 - funding.lfDDCosts) / 100);
    const invImp = deal.improvements * ((100 - funding.lfImprovements) / 100);
    const invCC = deal.purchaseClosingCosts * ((100 - funding.lfClosingCosts) / 100);
    const totalInvestorCapital = invPP + invDD + invImp + invCC;

    // Land finder investment
    const lfPP = deal.purchasePrice * (funding.lfPurchasePrice / 100);
    const lfDD = totalDD * (funding.lfDDCosts / 100);
    const lfImp = deal.improvements * (funding.lfImprovements / 100);
    const lfCC = deal.purchaseClosingCosts * (funding.lfClosingCosts / 100);
    const totalLFInvestment = lfPP + lfDD + lfImp + lfCC;

    return {
      totalDD,
      saleCommissionAmount,
      grossSalesRevenue,
      totalCosts,
      grossProfit,
      invPP, invDD, invImp, invCC, totalInvestorCapital,
      lfPP, lfDD, lfImp, lfCC, totalLFInvestment,
    };
  }, [deal, funding]);

  // Calculate tier results for a given set of tiers
  const calcTierResults = useCallback((tiers) => {
    return tiers.map(tier => {
      const investorSplit = tier.investorSplit;
      const investorAmount = calcs.grossProfit * (investorSplit / 100);
      const investorROI = calcs.totalInvestorCapital > 0 ? investorAmount / calcs.totalInvestorCapital : 0;

      // Annualized ROI — use end of range days if available, else start
      const endDays = parseNum(tier.endDay) || null;
      const startDays = parseNum(tier.startDay);
      const annualizedROI = endDays ? investorROI * (365 / endDays) : (startDays > 0 ? investorROI * (365 / startDays) : null);

      const lfSplit = 100 - investorSplit;
      const lfAmount = calcs.grossProfit * (lfSplit / 100);

      return {
        startDay: tier.startDay,
        endDay: tier.endDay,
        investorSplit,
        investorAmount,
        investorROI,
        annualizedROI,
        lfSplit,
        lfAmount,
      };
    });
  }, [calcs]);

  const optionAResults = useMemo(() => calcTierResults(optionATiers), [calcTierResults, optionATiers]);
  const optionBResults = useMemo(() => calcTierResults(optionBTiers), [calcTierResults, optionBTiers]);

  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfScenarios, setPdfScenarios] = useState([]);

  const handleDownloadPDF = () => {
    setShowPdfDialog(true);
    setPdfScenarios([]);
  };

  const confirmDownloadPDF = () => {
    if (pdfScenarios.length === 0) {
      toast.error('Select at least one scenario');
      return;
    }
    try {
      const doc = generateCalculatorPDF({
        deal,
        calcs,
        optionAResults,
        optionBResults,
        funding,
        scenarioName: scenarioName || undefined,
        scenarios: pdfScenarios,
      });
      const safeName = (scenarioName || 'JV_Deal_Proposal').replace(/[^a-zA-Z0-9_-]/g, '_');
      doc.save(`${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF downloaded');
      setShowPdfDialog(false);
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="space-y-6" data-testid="proposals-page">
      {/* Linked Project Banner */}
      {linkedProject && (
        <div className="flex items-center gap-3 bg-muted/40 border rounded-sm px-4 py-3" data-testid="linked-project-banner">
          <Link to={`/projects/${linkedProject.id}`}>
            <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8" data-testid="back-to-project-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Linked Project</span>
              <Badge variant="outline" className="text-[10px]">{linkedProject.status}</Badge>
            </div>
            <span className="text-sm font-semibold truncate block">{linkedProject.name}</span>
          </div>
          {currentProposalId && (
            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Saved</Badge>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Investment Proposals</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Model investor proposals with tiered profit splits and holding period scenarios
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Input
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            placeholder="Proposal name"
            className="h-9 w-48 text-sm"
            data-testid="scenario-name-input"
          />
          <Button
            variant="default"
            size="sm"
            onClick={handleSaveProposal}
            disabled={saving}
            className="rounded-sm gap-1.5 bg-[#1A2F23] hover:bg-[#1A2F23]/90"
            data-testid="save-proposal-btn"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : (currentProposalId ? 'Update' : 'Save')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLoadDialog(true)}
            className="rounded-sm gap-1.5"
            data-testid="load-proposal-btn"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Load
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="rounded-sm gap-1.5" data-testid="download-pdf-btn">
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleNewProposal} className="rounded-sm gap-1.5" data-testid="new-proposal-btn">
            <Plus className="w-3.5 h-3.5" /> New
          </Button>
          {currentProposalId && (
            <Button
              size="sm"
              onClick={handleOpenPromote}
              className="rounded-sm gap-1.5 bg-[#C68E5D] hover:bg-[#C68E5D]/90 text-white"
              data-testid="promote-to-partners-btn"
            >
              <Send className="w-3.5 h-3.5" /> Promote
            </Button>
          )}
        </div>
      </div>

      {/* Load Proposals Dialog */}
      {/* PDF Scenario Selection Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="sm:max-w-xs" data-testid="pdf-scenario-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Download Proposal PDF</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Select which scenario(s) to include:</p>
          <div className="space-y-3 py-2">
            {['A', 'B'].map(s => (
              <label
                key={s}
                className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${pdfScenarios.includes(s) ? 'border-[#1A2F23] bg-[#1A2F23]/5' : 'hover:bg-muted/30'}`}
                data-testid={`pdf-scenario-option-${s}`}
              >
                <Checkbox
                  checked={pdfScenarios.includes(s)}
                  onCheckedChange={(checked) => {
                    setPdfScenarios(prev => checked ? [...prev, s] : prev.filter(x => x !== s));
                  }}
                />
                <div>
                  <p className="text-sm font-medium">Scenario {s}</p>
                  <p className="text-xs text-muted-foreground">{s === 'A' ? 'Option A profit split tiers' : 'Option B profit split tiers'}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowPdfDialog(false)}>Cancel</Button>
            <Button
              size="sm"
              className="rounded-sm bg-[#1A2F23] hover:bg-[#1A2F23]/90 text-white"
              onClick={confirmDownloadPDF}
              disabled={pdfScenarios.length === 0}
              data-testid="pdf-confirm-download-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className="sm:max-w-md" data-testid="load-proposals-dialog">
          <DialogHeader>
            <DialogTitle>Saved Proposals</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
            {savedProposals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No saved proposals yet.</p>
            ) : savedProposals.map(p => (
              <div
                key={p.id}
                className={`flex items-center justify-between border rounded-sm p-3 hover:bg-muted/30 transition-colors ${p.id === currentProposalId ? 'border-emerald-500 bg-emerald-50/30' : ''}`}
                data-testid={`saved-proposal-${p.id}`}
              >
                <button className="flex-1 text-left" onClick={() => handleLoadProposal(p.id)}>
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {p.project_name && <span>{p.project_name} · </span>}
                    {new Date(p.updated_at).toLocaleDateString()}
                    {p.created_by && <span className="ml-1">· by {p.created_by}</span>}
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-red-600"
                  onClick={(e) => { e.stopPropagation(); handleDeleteProposal(p.id); }}
                  data-testid={`delete-proposal-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Promote to Partners Dialog */}
      <Dialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-[#C68E5D]" /> Promote to Partners
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Select Partners</Label>
              <div className="mt-2 max-h-72 overflow-y-auto space-y-1.5 border rounded-sm p-2">
                {activePartners.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-3">No active partners. Promote prospects first.</p>
                ) : activePartners.map(p => {
                  const selected = selectedPartnerIds.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`rounded-sm p-2 transition-colors ${selected ? 'bg-muted/40' : 'hover:bg-muted/30'}`}
                      data-testid={`promote-partner-${p.id}`}
                    >
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={(checked) => {
                            setSelectedPartnerIds(prev =>
                              checked ? [...prev, p.id] : prev.filter(id => id !== p.id)
                            );
                          }}
                          data-testid={`promote-partner-checkbox-${p.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground">{p.entity_name || p.email}</div>
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(p.total_available_capital || 0)}
                        </span>
                      </label>
                      {selected && (
                        <PartnerAllotmentRow
                          partnerId={p.id}
                          target={calcs.totalInvestorCapital || 0}
                          allotment={partnerAllotments[p.id]}
                          onChange={updateAllotment}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {selectedPartnerIds.length > 0 && (
                <AllotmentSummary
                  target={calcs.totalInvestorCapital || 0}
                  selectedIds={selectedPartnerIds}
                  allotments={partnerAllotments}
                />
              )}
              {activePartners.length > 0 && (
                <div className="flex items-center gap-2 mt-1.5">
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setSelectedPartnerIds(activePartners.map(p => p.id))}
                  >Select All</button>
                  <span className="text-xs text-muted-foreground">·</span>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => { setSelectedPartnerIds([]); setPartnerAllotments({}); }}
                  >Clear</button>
                  <span className="ml-auto text-xs text-muted-foreground">{selectedPartnerIds.length} selected</span>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground mt-2">
                Assign each partner a slice by <span className="font-medium">%</span> or <span className="font-medium">$</span>, or leave them <span className="font-medium">Open</span> to propose an amount you'll confirm. The deal commits once the capital target is filled.
              </p>
            </div>

            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description / Notes for Partners</Label>
              <textarea
                value={promoteDescription}
                onChange={e => setPromoteDescription(e.target.value)}
                placeholder="Brief description about this opportunity..."
                className="w-full border rounded-sm px-3 py-2 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-ring mt-1.5"
                data-testid="promote-description"
              />
            </div>

            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Profit Split Scenario</Label>
              <div className="flex gap-3 mt-1.5">
                {['A', 'B'].map(s => (
                  <label
                    key={s}
                    className={`flex-1 flex items-center gap-2 p-3 border rounded-sm cursor-pointer transition-colors ${
                      promoteScenario === s ? 'border-[#C68E5D] bg-[#C68E5D]/5' : 'hover:bg-muted/30'
                    }`}
                    data-testid={`promote-scenario-${s}`}
                  >
                    <input
                      type="radio"
                      name="scenario"
                      value={s}
                      checked={promoteScenario === s}
                      onChange={() => setPromoteScenario(s)}
                      className="accent-[#C68E5D]"
                    />
                    <div>
                      <div className="text-sm font-medium">Scenario {s}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {(s === 'A' ? optionATiers : optionBTiers).length} tier{(s === 'A' ? optionATiers : optionBTiers).length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Video URL (optional)</Label>
              <Input
                value={promoteVideoUrl}
                onChange={e => setPromoteVideoUrl(e.target.value)}
                placeholder="https://www.loom.com/share/..."
                className="h-9 rounded-sm mt-1.5"
                data-testid="promote-video-url"
              />
            </div>

            {/* PDF Attachments — uploaded to staging storage immediately on
                pick, then bundled into the promote request when the user
                clicks "Promote". Same file is attached to every partner email. */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  PDF Attachments (optional)
                </Label>
                <span className="text-[10px] text-muted-foreground">PDF only · max 25MB each · 30MB total per email</span>
              </div>
              <div className="mt-1.5 space-y-2">
                {promoteAttachments.length > 0 && (
                  <ul className="space-y-1.5" data-testid="promote-attachments-list">
                    {promoteAttachments.map(a => (
                      <li
                        key={a.id}
                        className="flex items-center gap-2 px-2.5 py-1.5 bg-muted/40 border rounded-sm text-xs"
                        data-testid={`promote-attachment-row-${a.id}`}
                      >
                        <Paperclip className="w-3.5 h-3.5 text-[#C68E5D] flex-shrink-0" />
                        <span className="flex-1 truncate font-medium">{a.filename}</span>
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          {!a.size ? '' : a.size < 1024 ? `${a.size} B` : a.size < 1024 * 1024 ? `${(a.size / 1024).toFixed(1)} KB` : `${(a.size / 1024 / 1024).toFixed(1)} MB`}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(a.id)}
                          className="text-muted-foreground hover:text-red-600 p-0.5"
                          aria-label="Remove attachment"
                          data-testid={`remove-attachment-${a.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <label
                  className={`flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed rounded-sm cursor-pointer text-xs transition-colors ${
                    uploadingAttachment
                      ? 'border-[#C68E5D]/40 bg-muted/30 cursor-not-allowed text-muted-foreground'
                      : 'border-muted-foreground/30 hover:border-[#C68E5D] hover:bg-[#C68E5D]/5 text-muted-foreground'
                  }`}
                  data-testid="promote-attachment-picker"
                >
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    onChange={handleAttachmentUpload}
                    disabled={uploadingAttachment}
                    data-testid="promote-attachment-file-input"
                  />
                  <Paperclip className="w-3.5 h-3.5" />
                  {uploadingAttachment
                    ? 'Uploading...'
                    : promoteAttachments.length === 0
                      ? 'Click to attach PDF document(s)'
                      : 'Add another PDF'}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoteDialog(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handlePromote}
              disabled={promoting || selectedPartnerIds.length === 0}
              className="bg-[#C68E5D] hover:bg-[#C68E5D]/90 text-white rounded-sm gap-1.5"
              data-testid="promote-confirm-btn"
            >
              <Send className="w-3.5 h-3.5" />
              {promoting ? 'Sending...' : `Promote to ${selectedPartnerIds.length} Partner${selectedPartnerIds.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SECTION 1: Deal Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Acquisition Costs */}
        <CollapsibleSection title="Acquisition Costs" icon={DollarSign} testId="acquisition-costs">
          <div className="space-y-4">
            <CurrencyInput
              label="Purchase Price"
              value={deal.purchasePrice}
              onChange={(v) => updateDeal('purchasePrice', v)}
              testId="input-purchase-price"
            />
            <Separator />
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Due Diligence</Label>
            <div className="grid grid-cols-3 gap-3">
              <CurrencyInput label="Survey" value={deal.survey} onChange={(v) => updateDeal('survey', v)} testId="input-survey" />
              <CurrencyInput label="Soil" value={deal.soil} onChange={(v) => updateDeal('soil', v)} testId="input-soil" />
              <CurrencyInput label="Other" value={deal.otherDD} onChange={(v) => updateDeal('otherDD', v)} testId="input-other-dd" />
            </div>
            <StatLine label="Total Due Diligence" value={fmt(calcs.totalDD)} highlight />
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <CurrencyInput label="Purchase Closing Costs" value={deal.purchaseClosingCosts} onChange={(v) => updateDeal('purchaseClosingCosts', v)} testId="input-purchase-cc" />
              <CurrencyInput label="Improvements" value={deal.improvements} onChange={(v) => updateDeal('improvements', v)} testId="input-improvements" />
            </div>
            <Separator />
            <StatLine label="Total Acquisition Cost" value={fmt(calcs.totalCosts)} highlight />
          </div>
        </CollapsibleSection>

        {/* Sale Projections */}
        <CollapsibleSection title="Sale Projections" icon={TrendingUp} testId="sale-projections">
          <div className="space-y-4">
            <CurrencyInput
              label="Estimated Sale Price"
              value={deal.estimatedSalePrice}
              onChange={(v) => updateDeal('estimatedSalePrice', v)}
              testId="input-sale-price"
            />
            <div className="grid grid-cols-2 gap-3">
              <CurrencyInput label="Sale Closing Costs" value={deal.saleClosingCosts} onChange={(v) => updateDeal('saleClosingCosts', v)} testId="input-sale-cc" />
              <PercentInput label="Sale Commission" value={deal.saleCommission} onChange={(v) => updateDeal('saleCommission', v)} testId="input-sale-commission" />
            </div>
            <Separator />
            <div className="bg-muted/30 rounded-sm p-4 space-y-1">
              <StatLine label="Sale Commission Amount" value={fmt(calcs.saleCommissionAmount)} />
              <StatLine label="Gross Sales Revenue" value={fmt(calcs.grossSalesRevenue)} highlight />
              <Separator className="my-2" />
              <StatLine
                label="Gross Profit"
                value={fmt(calcs.grossProfit)}
                highlight
                negative={calcs.grossProfit < 0}
              />
            </div>
          </div>
        </CollapsibleSection>
      </div>

      {/* SECTION 2: Funding Structure */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CollapsibleSection title="Land Finder Funding" icon={Briefcase} testId="lf-funding">
          <p className="text-xs text-muted-foreground mb-4">
            Set the percentage of each cost category that the Land Finder funds out of pocket. The remainder is funded by the investor.
          </p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 items-end">
              <PercentInput label="Purchase Price" value={funding.lfPurchasePrice} onChange={(v) => updateFunding('lfPurchasePrice', v)} testId="input-lf-pp" />
              <div className="text-right text-sm font-mono pb-2 text-muted-foreground">{fmt(calcs.lfPP)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <PercentInput label="DD Costs" value={funding.lfDDCosts} onChange={(v) => updateFunding('lfDDCosts', v)} testId="input-lf-dd" />
              <div className="text-right text-sm font-mono pb-2 text-muted-foreground">{fmt(calcs.lfDD)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <PercentInput label="Improvements" value={funding.lfImprovements} onChange={(v) => updateFunding('lfImprovements', v)} testId="input-lf-imp" />
              <div className="text-right text-sm font-mono pb-2 text-muted-foreground">{fmt(calcs.lfImp)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <PercentInput label="Closing Costs" value={funding.lfClosingCosts} onChange={(v) => updateFunding('lfClosingCosts', v)} testId="input-lf-cc" />
              <div className="text-right text-sm font-mono pb-2 text-muted-foreground">{fmt(calcs.lfCC)}</div>
            </div>
            <Separator />
            <StatLine label="Land Finder Total Investment" value={fmt(calcs.totalLFInvestment)} highlight />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Investor Capital" icon={Users} testId="investor-capital">
          <p className="text-xs text-muted-foreground mb-4">
            Calculated based on what the investor funds (the complement of Land Finder funding).
          </p>
          <div className="space-y-2">
            <StatLine label="Purchase Price" value={fmt(calcs.invPP)} />
            <StatLine label="Due Diligence Costs" value={fmt(calcs.invDD)} />
            <StatLine label="Improvements" value={fmt(calcs.invImp)} />
            <StatLine label="Closing Costs" value={fmt(calcs.invCC)} />
            <Separator />
            <StatLine label="Total Investor Capital" value={fmt(calcs.totalInvestorCapital)} highlight />
          </div>

          <Separator className="my-4" />
          <div className="bg-muted/30 rounded-sm p-4 space-y-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Deal Summary</span>
            <StatLine label="Total Capital Required" value={fmt(calcs.totalCosts)} />
            <StatLine label="Investor Contribution" value={fmt(calcs.totalInvestorCapital)} />
            <StatLine label="Land Finder Contribution" value={fmt(calcs.totalLFInvestment)} />
            <StatLine label="Gross Profit" value={fmt(calcs.grossProfit)} highlight negative={calcs.grossProfit < 0} />
          </div>
        </CollapsibleSection>
      </div>

      {/* SECTION 3: Profit Split Tiers + Results */}
      <Card className="rounded-sm border" data-testid="profit-split-results">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
            <CalcIcon className="w-4 h-4" />
            Profit Split Analysis
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Configure two tier structures and compare investor returns side by side
          </p>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="option-a" data-testid="tab-option-a">Option A</TabsTrigger>
              <TabsTrigger value="option-b" data-testid="tab-option-b">Option B</TabsTrigger>
              <TabsTrigger value="compare" data-testid="tab-compare">Compare</TabsTrigger>
            </TabsList>

            <TabsContent value="option-a">
              <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
                <div>
                  <TierEditor
                    tiers={optionATiers}
                    setTiers={setOptionATiers}
                    label="Option A — Tier Structure"
                    testIdPrefix="option-a"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 block">Results — Option A</Label>
                  <ResultsTable
                    tierResults={optionAResults}
                    totalInvestorCapital={calcs.totalInvestorCapital}
                    grossProfit={calcs.grossProfit}
                    testIdPrefix="option-a"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="option-b">
              <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
                <div>
                  <TierEditor
                    tiers={optionBTiers}
                    setTiers={setOptionBTiers}
                    label="Option B — Tier Structure"
                    testIdPrefix="option-b"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 block">Results — Option B</Label>
                  <ResultsTable
                    tierResults={optionBResults}
                    totalInvestorCapital={calcs.totalInvestorCapital}
                    grossProfit={calcs.grossProfit}
                    testIdPrefix="option-b"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="compare">
              <CompareView
                optionAResults={optionAResults}
                optionBResults={optionBResults}
                calcs={calcs}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// -- Compare View Component --
function CompareView({ optionAResults, optionBResults, calcs }) {
  return (
    <div className="space-y-6" data-testid="compare-view">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investor Comparison */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 block">
            Investor Returns Comparison
          </Label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Range</th>
                  <th className="text-center py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-blue-600" colSpan={2}>Option A</th>
                  <th className="text-center py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-amber-600" colSpan={2}>Option B</th>
                </tr>
                <tr className="border-b">
                  <th></th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Split</th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Amount</th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Split</th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Amount</th>
                </tr>
              </thead>
              <tbody>
                {optionAResults.map((a, i) => {
                  const b = optionBResults[i];
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-2 px-2 font-mono text-xs">{a.startDay}–{a.endDay || '∞'}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-blue-600">{a.investorSplit}%</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-blue-600">{fmt(a.investorAmount)}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{b ? `${b.investorSplit}%` : '—'}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{b ? fmt(b.investorAmount) : '—'}</td>
                    </tr>
                  );
                })}
                {optionBResults.length > optionAResults.length && optionBResults.slice(optionAResults.length).map((b, i) => (
                  <tr key={`b-extra-${i}`} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-2 font-mono text-xs">{b.startDay}–{b.endDay || '∞'}</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-muted-foreground">—</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-muted-foreground">—</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{b.investorSplit}%</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{fmt(b.investorAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Land Finder Comparison */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 block">
            Land Finder Net Profit Comparison
          </Label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Range</th>
                  <th className="text-center py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-blue-600" colSpan={2}>Option A</th>
                  <th className="text-center py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-amber-600" colSpan={2}>Option B</th>
                </tr>
                <tr className="border-b">
                  <th></th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Split</th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Net</th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Split</th>
                  <th className="text-right py-1 px-2 text-[10px] text-muted-foreground">Net</th>
                </tr>
              </thead>
              <tbody>
                {optionAResults.map((a, i) => {
                  const b = optionBResults[i];
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-2 px-2 font-mono text-xs">{a.startDay}–{a.endDay || '∞'}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-blue-600">{a.lfSplit}%</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-blue-600">{fmt(a.lfAmount)}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{b ? `${b.lfSplit}%` : '—'}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{b ? fmt(b.lfAmount) : '—'}</td>
                    </tr>
                  );
                })}
                {optionBResults.length > optionAResults.length && optionBResults.slice(optionAResults.length).map((b, i) => (
                  <tr key={`b-extra-${i}`} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-2 px-2 font-mono text-xs">{b.startDay}–{b.endDay || '∞'}</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-muted-foreground">—</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-muted-foreground">—</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{b.lfSplit}%</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-amber-600">{fmt(b.lfAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Gross Profit</div>
          <div className={`text-lg font-semibold font-mono ${calcs.grossProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(calcs.grossProfit)}</div>
        </div>
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Investor Capital</div>
          <div className="text-lg font-semibold font-mono">{fmt(calcs.totalInvestorCapital)}</div>
        </div>
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">LF Investment</div>
          <div className="text-lg font-semibold font-mono">{fmt(calcs.totalLFInvestment)}</div>
        </div>
        <div className="bg-muted/30 rounded-sm p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Total Capital</div>
          <div className="text-lg font-semibold font-mono">{fmt(calcs.totalCosts)}</div>
        </div>
      </div>
    </div>
  );
}

export default Calculator;
